import React, { useContext, useEffect, useMemo } from "react";
import SEO from "@/components/SEO";
import {
  HeroSection,
  ProductSection,
  AboutSection,
  UpsSection,
  GensetsSection,
  BatteryStorageSection,
  BatterySection,
  SolarSection,
  ServiceSection,
  WasteBatterySection,
  CareerSection,
  TrustedBySection,
} from "@/components/sections";
import { HomepageContext } from "@/components/header/ConnectedHeader";
import { i18n } from "./homepage.i18n";
import styles from "./HomePage.module.css";

const HomePage: React.FC = () => {
  const { language } = useContext(HomepageContext);
  const currentLang = (language || "cs") as keyof typeof i18n.pageTitle;

  // Define sections with their background preferences
  const allSections = useMemo(
    () => [
      { component: HeroSection, name: "hero", defaultBg: "none" },
      { component: ProductSection, name: "product", defaultBg: "soft" },
      { component: AboutSection, name: "about", defaultBg: "none" },
      { component: UpsSection, name: "ups", defaultBg: "soft" },
      { component: GensetsSection, name: "gensets", defaultBg: "none" },
      {
        component: BatteryStorageSection,
        name: "battery-storage",
        defaultBg: "soft",
      },
      { component: BatterySection, name: "battery", defaultBg: "none" },
      { component: SolarSection, name: "solar", defaultBg: "soft" },
      { component: ServiceSection, name: "service", defaultBg: "none" },
      // Include WasteBatterySection for Czech and English
      ...(language === "cs" || language === "en"
        ? [
            {
              component: WasteBatterySection,
              name: "waste-battery",
              defaultBg: "soft",
            },
          ]
        : []),
      {
        component: CareerSection,
        name: "career",
        defaultBg: language === "cs" || language === "en" ? "none" : "soft",
      },
      { component: TrustedBySection, name: "trusted", defaultBg: "none" },
    ],
    [language]
  );

  useEffect(() => {
    const shouldScrollToBottom = sessionStorage.getItem("scrollToBottom");
    if (shouldScrollToBottom) {
      sessionStorage.removeItem("scrollToBottom");
      setTimeout(() => {
        const contactsLink = document.querySelector(
          "header nav ul li:last-child a"
        );
        if (contactsLink) {
          (contactsLink as HTMLElement).click();
        }
      }, 500);
    }
  }, []);

  return (
    <div className="home-page">
      <SEO
        title={i18n.pageTitle[currentLang]}
        description={i18n.pageDescription[currentLang]}
        isHomePage={true}
      />
      {allSections.map(({ component: Component, name }, index) => {
        const shouldHaveSoftBg = index % 2 === 1; // Every second section gets soft background
        const bgClass = shouldHaveSoftBg
          ? styles.sectionSoft
          : styles.sectionNone;

        return (
          <div key={name} className={bgClass}>
            <Component />
          </div>
        );
      })}
    </div>
  );
};

export default HomePage;
